/**
 * src/constants/schema.js
 * Single source of truth for collection names, status enums, and query limits.
 * Import these everywhere — never hard-code collection strings or status values.
 */
import { serverTimestamp } from 'firebase/firestore';
import { PAYROLL_COLLECTIONS } from './payroll';

// ── Collection names ──────────────────────────────────────────────────────────
export const COLLECTIONS = {
  USERS:                    'users',
  PROJECTS:                 'projects',
  QUOTATIONS:               'quotations',              // legacy bid/proposal docs
  SALES_INVOICE_QUOTATIONS: 'salesInvoiceQuotations',  // contract & progress-claim docs
  SALES_PROJECT_INVOICES:   'salesProjectInvoices',    // formal tax invoices
  CLAIMS:                   'claims',                  // staff expense reimbursements
  DEFECT_LISTS:             'defectLists',
  DAILY_TRANSPORT:          'dailyTransport',
  FOLLOW_UPS:               'followUps',           // legacy: personal action items (Sprints)
  PROJECT_FOLLOW_UPS:       'projectFollowUps',    // physical item tracking per project
  PROJECT_FOLLOW_UP_SETTINGS: 'projectFollowUpSettings', // global feature settings doc
  PROJECT_STARS:              'projectStars',             // per-user starred/favourite projects
  SPRINTS:                  'sprints',
  TEAMS:                    'teams',
  VEHICLES:                 'vehicles',
  ACCOMMODATIONS:           'accommodations',
  LEAVE_RECORDS:            'leaveRecords',
  MATERIALS:                'materialProducts',
  MATERIAL_CATEGORIES:      'materialCategories',
  // Sets and parts have no collection of their own: a set's composition is the
  // `parts[]` array on its materialProducts doc (see utils/partsCalc.js). A
  // listing is a "set" or a "part" only by virtue of those arrays — the role is
  // DERIVED on read, never stored, so staff never pick one.
  // RETIRED 2026-08 — Product Catalogue module removed, superseded by the Works
  // Catalogue below. The live data is deliberately retained but has no reader
  // and is blocked in firestore.rules / storage.rules.
  PRODUCTS:                 'productCatalogueProducts',
  PRODUCT_CATEGORIES:       'productCatalogueCategories',
  SUPPLIERS:                'suppliers',
  SOPS:                     'sops',
  SOP_CATEGORIES:           'sopCategories',
  WIP_ADJUSTMENTS:          'wipAdjustments',
  SECURITY_LOGS:            'securityLogs',
  // ── Project Costing module ───────────────────────────────────────────────
  PROJECT_COSTINGS:               'projectCostings',
  PROJECT_WAGES:                  'projectWages',
  MATERIAL_COST_WIP_ADJUSTMENTS:  'materialCostWipAdjustments',
  // ── Invoice Payments (AR payment tracking per invoice) ───────────────────
  INVOICE_PAYMENTS:               'invoicePayments',
  // ── Sales Adjustments (out-of-scope money: waivers, discounts, write-offs) ─
  SALES_ADJUSTMENTS:              'salesAdjustments',
  // ── General Expenses (company-wide consumables: tape, rags, blades) ───────
  // Pooled per year and, from Phase 6c, spread across projects by revenue
  // share. Years are NEVER locked — entry and edit stay open in any year.
  GENERAL_EXPENSES:               'generalExpenses',
  // Phase 6b cache: one doc per year holding the expense pool and the revenue
  // denominator. Written ONLY by Cloud Functions (rules deny all client
  // writes). Holds no per-project allocation — a project's share is arithmetic
  // done at read time.
  YEARLY_AGGREGATES:              'yearlyAggregates',
  GENERAL_EXPENSE_SETTINGS:       'generalExpenseSettings',
  // ── App-wide global settings ─────────────────────────────────────────────
  APP_SETTINGS:                   'appSettings',
  // ── Purchasing → Inventory → Project Costing framework ───────────────────
  PURCHASING_ENTRIES:             'purchasingEntries',      // LEGACY flat model — superseded by PO/DO/Invoice below, kept for migration
  INVENTORY_STOCK_ITEMS:          'inventoryStockItems',    // LEGACY — superseded by INVENTORY_ITEMS below, kept for migration
  INVENTORY_MOVEMENTS:            'inventoryMovements',     // LEGACY — superseded by INVENTORY_LEDGER below, kept for migration
  INVENTORY_TRANSACTIONS:         'inventoryTransactions',  // LEGACY — superseded by INVENTORY_LEDGER below, kept for migration
  INVENTORY_ITEMS:                'inventoryItems',         // current stock balances (factory only)
  INVENTORY_LEDGER:               'inventoryLedger',        // unified append-oriented audit trail: delivery / withdrawal / adjustment / opening balance
  INVENTORY_SKU_LOCKS:            'inventorySkuLocks',      // genesis lock: one doc per SKU, created the first time that SKU is ever tracked
  PURCHASE_AUDIT_LOGS:            'purchaseAuditLogs',      // LEGACY field-level audit trail — superseded by embedded editHistory[]
  // ── Purchasing revamp: header/lines PO model (2026) ───────────────────────
  PURCHASE_ORDERS:                'purchaseOrders',       // PO header + lineItems[]
  DELIVERY_ORDERS:                'deliveryOrders',       // DO records, can span multiple POs
  PURCHASE_INVOICES:              'purchaseInvoices',      // invoice records, can span multiple POs — named distinctly from salesInvoices/salesProjectInvoices
  BRIDGE_STATUS:                  'bridgeStatus',          // one doc per source record: did the server bring linked functions in line? (functions/bridge/)
  COUNTERS:                       'counters',              // atomic sequence docs (e.g. counters/purchaseOrders → { seq })
  // ── Follow Ups logistics hub ─────────────────────────────────────────────
  PROJECT_DRIVER_JOBS:            'projectDriverJobs',   // driver delivery jobs with phases
  PROJECT_STOCK_ITEMS:            'projectStockItems',   // project stock list
  PROJECT_SPRINTS:                'projectSprints',      // sprint blocks (per-project or global)
  // ── Follow Up Lists (new ownership model) ────────────────────────────────
  FOLLOW_UP_LISTS:                'followUpLists',       // manually-created follow up list containers
  // ── Notes ────────────────────────────────────────────────────────────────
  NOTES:                          'notes',               // personal + project-linked PM notes
  // ── Knowledge Base ───────────────────────────────────────────────────────
  // RETIRED 2026-08 — Carpentry Details module removed, superseded by
  // CATALOGUE_DETAILS below. Live data retained, no reader, rules blocked.
  CARPENTRY_DETAILS:              'carpentryDetails',
  CARPENTRY_DETAIL_FILES:         'files',               // subcollection: carpentryDetails/{id}/files
  // ── Work Catalogue ───────────────────────────────────────────────────────
  // Photos-first (2026-10): one doc per photo, grouped by project. See
  // src/services/cataloguePhotoService.js.
  CATALOGUE_PHOTOS:               'cataloguePhotos',
  // LEGACY — the items/details model it replaced. No reader; rules read-only.
  // Data kept for possible reuse when tags are designed.
  CATALOGUE_ITEMS:                'catalogueItems',
  CATALOGUE_DETAILS:              'catalogueDetails',
  CATALOGUE_TYPES:                'catalogueTypes',
  CATALOGUE_TAGS:                 'catalogueTags',

  // Admin-managed vocabulary classifying a catalogue item as a consumable
  // material vs. reusable equipment. Distinct from CATALOGUE_TYPES above, which
  // classifies finished Works Catalogue pieces — the two never mix.
  ITEM_TYPES:                     'itemTypes',

  // ── Payroll (Attendance rebuild) ─────────────────────────────────────────
  // Spread from src/constants/payroll.js so the names have one home. The
  // dependency runs one way only: payroll.js must stay Firebase-free (the pure
  // pay engine and the Excel acceptance harness both import it), and this file
  // imports `serverTimestamp` — so payroll.js may never import schema.js.
  ...PAYROLL_COLLECTIONS,
};

// ── Purchasing / Inventory framework ─────────────────────────────────────────
// Purchasing feeds Inventory (on DO number) and Project Costing (on project link
// or inventory withdrawal). Business rules:
//   • DO number    → controls QUANTITY movement into Inventory.
//   • Invoice + price → controls official COST amount.
//   • Dates are supporting info only — never the trigger.
//   • Item Type (COLLECTIONS.ITEM_TYPES) is a textual classification only —
//     never a logic switch. Phase 1 labels; later phases may act on it.

// Lifecycle status for a purchasing entry (batch).
// Derived from field state, but also stored as a snapshot for queries.
export const PURCHASE_STATUS = {
  PENDING_DO:      'Pending DO',
  PENDING_INVOICE: 'Pending Invoice',
  COMPLETE:        'Complete',
};

// Type of an InventoryTransaction record.
// LEGACY — superseded by INVENTORY_LEDGER_TYPE above, kept for migration.
export const INVENTORY_TRANSACTION_TYPE = {
  RESTOCK: 'restock',  // goods received into Factory Inventory (DO trigger)
  DRAW:    'draw',     // material drawn to a project or worker
  RETURN:  'return',   // returned from project / site back to Factory Inventory
};

/* PURCHASING_ITEM_TYPE / PURCHASING_ITEM_TYPE_OPTIONS ('Inventory' /
 * 'Project-Use' / 'To be Delivered') were removed here. They badged the legacy
 * flat `purchasingEntries` model, which was deleted at the Commit 7 cutover,
 * and had zero references left in the codebase. Not to be confused with the
 * new Item Type vocabulary (COLLECTIONS.ITEM_TYPES — Material vs
 * Equipment/Machinery), which classifies the item itself rather than the state
 * of a purchase. */

/**
 * Provenance of an inventory ledger entry's `unitPriceAtTime`.
 *
 * Purchasing used to stamp the PO line's hand-entered Est. Price onto a
 * delivery. That input was removed (staff reported it as redundant — estimates
 * are wrong until the invoice arrives), so a delivery now resolves the price
 * from the Material Catalogue's cost history instead. This marker records
 * WHICH of those a stored price came from.
 *
 * It is required, not cosmetic: a later phase will backfill confirmed invoice
 * prices over provisional ones, and it can only do that safely if it can tell
 * a provisional catalogue estimate from a price that is already confirmed.
 * Without the marker that phase becomes an unsafe guess.
 *
 * NONE means no price is known — the entry stores null, never 0. Keep those
 * distinct: 0 means genuinely free, null means unknown.
 */
export const PRICE_SOURCE = {
  CATALOGUE: 'catalogue',  // provisional — latest cost entry on the catalogue item
  INVOICE:   'invoice',    // confirmed — an actual supplier invoice line
  NONE:      'none',       // unknown — ad-hoc item, or no cost history to draw on
  // Typed in on the Issue / Collect / Break Down form because the item had no
  // price at all when stock was drawn. Provisional, like CATALOGUE.
  MANUAL_ESTIMATE: 'manual_estimate',
  // Seeded by "Set opening balance". Treated as confirmed: it is the business's
  // own statement of what the stock it already held cost.
  OPENING_BALANCE: 'opening_balance',
};

/**
 * Whether the price on an Inventory-sourced Project Costing row is final.
 *
 * ESTIMATE rows were charged at a provisional price (catalogue estimate or a
 * typed-in one) because the stock was drawn before its invoice arrived. The
 * `repriceInventoryOnInvoice` Cloud Function moves them to CONFIRMED at the
 * invoice price once the invoice is recorded.
 */
export const INVENTORY_PRICE_STATUS = {
  CONFIRMED: 'confirmed',
  ESTIMATE:  'estimate',
};

/**
 * Where an issue / collection is going. An issue must name a project or say,
 * explicitly, that it is for factory use — a blank destination is refused in
 * the form, the service and firestore.rules (`issuedFor` on the ledger entry).
 */
export const ISSUED_FOR = {
  PROJECT: 'project',
  FACTORY: 'factory',
};

/**
 * The Item Type a PO line falls back to when the catalogue has not classified
 * the item (or there is no catalogue item at all) — tiers 2 and 3 of
 * utils/itemTypeResolution.js.
 *
 * Lives here rather than in itemTypeService so the pure resolution logic (and
 * its tests) don't have to import a module that initialises Firebase. The
 * vocabulary itself is admin-managed in COLLECTIONS.ITEM_TYPES; this is only
 * the default selection.
 */
export const DEFAULT_ITEM_TYPE = 'Material';

// Inventory movement types — appended to the movement audit trail.
// LEGACY — superseded by INVENTORY_LEDGER_TYPE below, kept for migration.
export const INVENTORY_MOVEMENT_TYPE = {
  STOCK_IN_FROM_PURCHASING: 'STOCK_IN_FROM_PURCHASING',
  ISSUED_TO_PROJECT:        'ISSUED_TO_PROJECT',
  ADJUSTMENT:               'ADJUSTMENT',
};

// Entry types on the unified `inventoryLedger` collection.
//   delivery        — stock received (Purchasing DO). Only Factory-location
//                     deliveries affect currentQty; other locations are audit-only.
//   withdrawal      — stock issued to a person/project. Requires Inventory Full.
//   collection      — stock coming BACK from a person/project: excess returned,
//                     a design change that swapped the ironmongery, the wrong
//                     item taken. The exact inverse of `withdrawal` (positive
//                     qtyChange), and deliberately NOT an `adjustment`: an
//                     adjustment reconciles a variance nobody can account for,
//                     whereas a collection is a real, intended movement of known
//                     goods from a known person. Keeping them apart is what lets
//                     an item's history show how much came back, and keeps
//                     returns out of the Costing page's shrinkage figures.
//                     Every "value issued" aggregation in inventoryCalc.js nets
//                     collections against withdrawals — see isCollection there.
//   adjustment      — physical-count correction. `reason` is mandatory. Requires
//                     Inventory Full.
//   opening_balance — the stock a SKU already held before the system started
//                     tracking it. A one-time genesis event: exactly one per SKU,
//                     ever, and only while that SKU has no history at all (see
//                     INVENTORY_SKU_LOCKS). Not a delivery (no supplier/PO — it must
//                     never appear in Purchasing spend) and not an adjustment (it
//                     isn't correcting a variance, it IS the starting point). Being
//                     a baseline rather than a transaction, it is deliberately
//                     excluded from every period-over-period figure on the Factory
//                     Stock Costing page — see inventoryCalc.js, where the delivery/
//                     withdrawal/adjustment predicates are all strict equality so a
//                     new type falls through each aggregation by construction.
//   break_down      — a set opened on the floor: −N of the set SKU, +N×qty of each
//                     part SKU, at the same location, in one transaction. The
//                     composition comes from the set's `parts[]`. Under
//                     SET_COSTING_MODE.WHOLE_SET the set's whole cost is charged
//                     once, here, and the parts arrive carrying no value at all
//                     (see `zeroValueQty` on inventoryItems) so issuing them later
//                     cannot charge a second time for the same money.
//   assemble        — the exact inverse: −qty of each part, +1 of the set. Posts no
//                     cost and names no project, so unlike break_down it is right to
//                     let it fall out of every money aggregation.
//
// IMPORTANT, and the opposite of the opening_balance note above: strict-equality
// exclusion is the correct default for a type that moves no money, and the WRONG
// default for one that does. `break_down` writes a real projectCostings row, so
// every figure that reconciles against Project Costing has to opt it back in
// explicitly — inventoryCalc.js names each one. Adding a further money-moving type
// means walking that list again; adding an inert one (like `assemble`) means
// walking nothing.
export const INVENTORY_LEDGER_TYPE = {
  DELIVERY:        'delivery',
  WITHDRAWAL:      'withdrawal',
  COLLECTION:      'collection',
  ADJUSTMENT:      'adjustment',
  OPENING_BALANCE: 'opening_balance',
  BREAK_DOWN:      'break_down',
  ASSEMBLE:        'assemble',
};

// Which leg of a grouped movement an entry is. Every entry produced by one
// break-down or assemble carries the same `movementGroupId` — the SET entry's own
// document id, so a group needs no document of its own and can be found with a
// single equality filter (no composite index).
//
// A break-down writes: one SET entry (type break_down, negative), one PART entry
// per component (type break_down, positive), and one ordinary `withdrawal` per
// part with a used quantity above zero. Those withdrawals are real issues and are
// deliberately NOT a new type — Project Costing, the usage tools and the person's
// outstanding-items list should all treat them exactly like any other issue.
export const INVENTORY_MOVEMENT_ROLE = {
  SET:  'set',
  PART: 'part',
};

// Ceiling on parts in a single break-down/assemble transaction, and on a set's
// `parts[]` (mirrored by validParts() in firestore.rules). A real set in this
// business is 2–8 parts; this is ~2.5× the worst realistic case and doubles as
// protection against a mis-entered parts list. The transaction stays far under
// Firestore's 500-write limit (≤ 2 + 4N writes, ≤ N + 1 reads). If this ever needs
// to exceed ~50, move the write to a Cloud Function with the Admin SDK rather than
// splitting it across client transactions, which would make a PARTIAL break-down
// possible — stock out of the set with the parts never arriving.
export const MAX_BREAK_DOWN_CHILDREN = 20;

// How a broken-down set's cost is apportioned.
//   whole_set  — the set's entire cost is charged once at break-down; parts arrive
//                at zero value. Simple, exact against Project Costing, and honest
//                so long as sets are broken open for a known job.
//   cost_share — RESERVED, not implemented. Each part would carry a share of the
//                set price from `costShare` on its `parts[]` entry.
export const SET_COSTING_MODE = {
  WHOLE_SET:  'whole_set',
  COST_SHARE: 'cost_share',
};

export const DEFAULT_SET_COSTING_MODE = SET_COSTING_MODE.WHOLE_SET;

// Designation on an `inventoryItems` doc / `inventoryLedger` entry — whose
// stock this is. Follows the PO line's Project field (factory_stock → 'factory';
// a projectId → 'project'), independent of Place of Storage. Only 'factory'
// items carry a real currentQty balance; 'project' is ledger-only (audit trail
// for project-designated deliveries), never its own withdrawable stock-on-hand.
export const INVENTORY_DESIGNATION = {
  FACTORY: 'factory',
  PROJECT: 'project',
};

// Source tag written onto projectCostings rows created by this framework, so the
// Project Costing UI can display them while we guard against duplicate inserts.
export const COST_ROW_SOURCE = {
  PURCHASING: 'purchasing',
  INVENTORY:  'inventory',
};

// Purchasing units (shared with Inventory). Same vocabulary as follow-ups/MCJ.
export const PURCHASING_UNIT_OPTIONS = [
  { value: 'pcs',   label: 'pcs' },
  { value: 'nos',   label: 'nos' },
  { value: 'box',   label: 'box' },
  { value: 'set',   label: 'set' },
  { value: 'roll',  label: 'roll' },
  { value: 'pkt',   label: 'pkt' },
  { value: 'ctn',   label: 'ctn' },
  { value: 'yard',  label: 'yard' },
  { value: 'metre', label: 'metre' },
];

// ── Purchasing revamp: PO/DO/Invoice header-lines model ──────────────────────
// A PO line's `designation` is EITHER the sentinel below OR a projectId string —
// it answers "whose cost is this", independent of `location` ("where is it
// physically right now"). See PURCHASE_LOCATION_OPTIONS below for location.
export const PURCHASE_DESIGNATION = {
  FACTORY_STOCK: 'factory_stock',
};

// Display label for the factory-stock sentinel, everywhere Purchasing shows a
// line's Project. "Factory / Inventory" rather than "Factory" because the
// business reads the option as "this is bought for our own stock, not a
// project" — and because PURCHASE_LOCATION.FACTORY below is ALSO the string
// 'Factory', naming the physical place a delivery sits. The two are different
// questions (whose cost / where is it) and the location label is deliberately
// NOT renamed. Resolve a line through purchasingCalc.purchaseDesignationLabel
// rather than reading this map directly.
export const PURCHASE_DESIGNATION_LABELS = {
  [PURCHASE_DESIGNATION.FACTORY_STOCK]: 'Factory / Inventory',
};

// Physical location of a delivered item. Distinct from STORAGE_LOCATION_OPTIONS
// above, which belongs to the separate Follow-Ups item-tracking feature — do not
// merge the two lists, they serve different modules.
// 'Factory' is the canonical "counts as factory stock" location. It has been
// renamed twice: FACTORY_STORAGE / 'Factory Storage' → 'Storeroom' (2026-07-14)
// → 'Factory' (2026-08-11, to match how the business actually speaks).
// inventoryService.js keys its stock-in/stock-out logic off this exact value, so
// a third rename means updating that file's `affectsBalance` check, adding the
// old string to PURCHASE_LOCATION_ALIASES below, AND migrating the stored data.
export const PURCHASE_LOCATION = {
  OFFICE:         'Office',
  FACTORY:        'Factory',
  SITE:           'Site',
};

export const PURCHASE_LOCATION_OPTIONS = [
  { value: PURCHASE_LOCATION.OFFICE,  label: 'Office' },
  { value: PURCHASE_LOCATION.FACTORY, label: 'Factory' },
  { value: PURCHASE_LOCATION.SITE,    label: 'Site' },
];

/* ── Legacy aliases for the same physical place ──────────────────────────────
 * Superseded spellings of the factory location. These are NOT retired values
 * (see PURCHASE_LOCATION_RETIRED below) — a retired value names a genuinely
 * different place that is not factory stock, whereas these are the *same* place
 * under an older name, and must keep resolving to factory stock.
 *
 * Everything that compares a location against PURCHASE_LOCATION.FACTORY, or
 * renders one, normalizes through the helper first. That makes the rename safe
 * in either order — a doc that adminTools/migrateStoreroomToFactory.js has not
 * reached yet still counts as stock and still reads "Factory" on screen. Without
 * it, editing an unmigrated DO would see affectsBalance=false against a ledger
 * entry that WAS factory stock, and reverse quantity that never left the floor.
 */
export const PURCHASE_LOCATION_ALIASES = Object.freeze({
  'Storeroom':       PURCHASE_LOCATION.FACTORY,  // 2026-07-14 → 2026-08-11
  'Factory Storage': PURCHASE_LOCATION.FACTORY,  // pre-2026-07-14
});

/** Canonical form of a stored location string. Pass-through for anything
 *  already current, retired, or empty. */
export function normalizePurchaseLocation(v) {
  return PURCHASE_LOCATION_ALIASES[v] || v;
}

/* ── Retired location values (2026-08) ───────────────────────────────────────
 * 'with PM' / 'with Carpenter' / 'with Architect-Designer' / 'with Supplier'
 * were removed from the picker: WHO an item is for is now captured once, up
 * front, by the PO line's "Who Order / Who Use" field (assignedToName), so
 * re-answering it at delivery time was duplicate entry. Place of Storage is
 * now purely about WHERE the stock physically sits.
 *
 * They are RETIRED, not deleted, and that distinction is load-bearing:
 * deliveryOrderService.sanitizeLine coerces any unrecognised location to
 * PURCHASE_LOCATION.FACTORY, and Factory is the one value inventoryService
 * treats as factory stock (see its `affectsBalance` check). Dropping these
 * strings from the vocabulary would therefore convert every historical
 * "with PM" delivery into factory stock the next time its DO was edited —
 * inventing quantity that never arrived. Keep them accepted on write and
 * displayable on read; just never offer them for new entries.
 */
export const PURCHASE_LOCATION_RETIRED = Object.freeze([
  'with PM',
  'with Carpenter',
  'with Architect/Designer',
  'with Supplier',
]);

/* Facet options for retired values, so the Purchasing filter panel can still
   narrow to historical rows that hold one. Callers pass these alongside
   PURCHASE_LOCATION_OPTIONS through a count-and-drop helper, so a retired
   value that no longer appears in any record simply never renders. */
export const PURCHASE_LOCATION_RETIRED_OPTIONS = PURCHASE_LOCATION_RETIRED.map(
  v => ({ value: v, label: v }),
);

/** Every location string accepted on write — current vocabulary plus retired. */
export const PURCHASE_LOCATION_ALL_VALUES = Object.freeze([
  ...Object.values(PURCHASE_LOCATION),
  ...PURCHASE_LOCATION_RETIRED,
]);

// Per-line derived status (never stored — computed from qtyOrdered vs
// qtyDelivered/qtyInvoiced in purchasingCalc.js).
export const PO_DELIVERY_STATUS = {
  PENDING:            'Pending',
  PARTIALLY_DELIVERED:'Partially Delivered',
  FULLY_DELIVERED:    'Fully Delivered',
};

export const PO_INVOICE_STATUS = {
  PENDING:            'Pending',
  PARTIALLY_INVOICED: 'Partially Invoiced',
  FULLY_INVOICED:     'Fully Invoiced',
};

// ── Supplier List ───────────────────────────────────────────────────────────
// What KIND of counterparty a `suppliers` doc is — we buy materials from one and
// buy labour from the other, and the two are never interchangeable on a cost row.
// Required on every supplier saved through the UI; older docs may still be null.
//
// The stored values are these snake_case keys, not the display labels, and they
// are enforced server-side (`supplierCategory in ['material_supplier','sub_con',
// null]` in firestore.rules) — do not rename them without changing the rules.
export const SUPPLIER_CATEGORY = {
  MATERIAL: 'material_supplier',
  SUBCON:   'sub_con',
};

export const SUPPLIER_CATEGORY_LABELS = {
  [SUPPLIER_CATEGORY.MATERIAL]: 'Material Supplier',
  [SUPPLIER_CATEGORY.SUBCON]:   'Sub-Con',
};

// Badge colour per category, as `tag-color-*` class suffixes. Shared so the same
// blue/purple reads the same wherever a category is shown (Supplier List table,
// Project Costing's Company / Brand dropdowns).
export const SUPPLIER_CATEGORY_COLOR = {
  [SUPPLIER_CATEGORY.MATERIAL]: 'blue',
  [SUPPLIER_CATEGORY.SUBCON]:   'purple',
};

export const SUPPLIER_CATEGORY_OPTIONS = [
  { value: SUPPLIER_CATEGORY.MATERIAL, label: SUPPLIER_CATEGORY_LABELS[SUPPLIER_CATEGORY.MATERIAL] },
  { value: SUPPLIER_CATEGORY.SUBCON,   label: SUPPLIER_CATEGORY_LABELS[SUPPLIER_CATEGORY.SUBCON] },
];

// Supplier "Type" tags that name a sheet material — the board-goods a supplier
// sells by the sheet rather than by the metre, hour or piece. These are singled
// out because later work keys off the set; for now the only effect is that they
// always render maroon, whatever colour the tag carries in `globalSupplierTags`.
// Matched case-insensitively on the tag text, since types are free-typed.
export const SHEET_MATERIAL_TAGS = ['Plywood', 'Laminate', 'Veneer', 'Melamine'];

const SHEET_MATERIAL_TAG_SET = new Set(SHEET_MATERIAL_TAGS.map(t => t.toLowerCase()));

export const isSheetMaterialTag = (tag) =>
  SHEET_MATERIAL_TAG_SET.has((tag || '').trim().toLowerCase());

// Colour a supplier Type tag resolves to: the sheet-material override first,
// otherwise whatever the user picked for it, otherwise the neutral default.
export const resolveSupplierTagColor = (tag, tagColors = {}) =>
  isSheetMaterialTag(tag) ? 'maroon' : (tagColors[tag] || 'default');

// ── Project Costing ─────────────────────────────────────────────────────────
// Cost rows live in `projectCostings`, one doc per material OR stemcrafter row.
// Master List = all rows; per-project tabs = filter by projectId + costType.
export const COST_TYPE = {
  MATERIAL:    'material',
  STEMCRAFTER: 'stemcrafter',
  SUBCON:      'subcon',
};

export const COST_TYPE_OPTIONS = [
  { value: COST_TYPE.MATERIAL,    label: 'Material Cost' },
  { value: COST_TYPE.SUBCON,      label: 'Sub-Con Payment' },
  { value: COST_TYPE.STEMCRAFTER, label: "Stemcrafter's Material Costs" },
];

/**
 * Invoice-level adjustments on Project Costing invoices (rowKind 'adjustment'
 * rows — see groupRowsIntoInvoices in utils/projectCostingCalc.js).
 *
 * A FIXED list: the value is stored on the row and later reporting groups on
 * it, so renaming a value would orphan every record already carrying it. Add
 * new categories; never rename or remove one. `direction` is the default the
 * form preselects — staff can flip it.
 */
export const COST_ADJUSTMENT_CATEGORIES = Object.freeze([
  { value: 'discount',  label: 'Discount',             direction: 'decrease' },
  { value: 'contra',    label: 'Contra / Back-charge', direction: 'decrease' },
  { value: 'retention', label: 'Retention',            direction: 'decrease' },
  { value: 'rounding',  label: 'Rounding',             direction: 'decrease' },
  { value: 'shipping',  label: 'Shipping & Delivery',  direction: 'increase' },
  { value: 'misc',      label: 'Miscellaneous',        direction: 'increase' },
]);

/** Roles that may void a cost adjustment — mirror of salesSeniorAccess() in firestore.rules. */
export const COST_ADJUSTMENT_VOID_ROLES = Object.freeze(['bosscontrol', 'boss', 'accountant', 'admin']);

// Sub-Con claim → subcontractor quotation link.
//
// A subcon's agreed sum is a `salesInvoiceQuotations` doc of type
// SALES_QUOTATION_TYPE.SUB_CON; each claim they bill against it is a
// projectCostings row with costType 'subcon' carrying that doc's id here.
//
// Empty string is a first-class, expected value — not missing data. It means a
// claim arrived with no quotation on file (the subcon was engaged verbally, or
// the quote went to the PM directly and never reached the office). Those rows
// surface under "No quotation on file" on the Sub-Con Payments tab, which is
// itself the signal worth seeing. They can be attached to a quotation later.
export const SUBCON_NO_QUOTATION = '';

// Soft-delete sentinel — matches the existing `status` convention used by other
// collections in this app (e.g. claims, daily transport).
export const COST_ROW_STATUS = {
  ACTIVE:  'active',
  DELETED: 'deleted',
};

// Payment Type tags used inside each cost row's `payments[]` array.
export const PAYMENT_TYPE_OPTIONS = [
  'Initial Payment',
  'Partial Payment',
  'Full Payment',
  'Final Payment',
  'Deposit',
  'Balance Payment',
];

// Margin status thresholds (% of revenue). Tweakable here without touching UI.
export const MARGIN_STATUS_THRESHOLDS = {
  HEALTHY:   15, // >= 15% margin
  WATCH:      5, // 5–15% margin
  LOSS_RISK:  0, // 0–5% margin (above 0)
  // < 0% → 'loss'
};

// ── Project ───────────────────────────────────────────────────────────────────
// Re-exported from the canonical source — import directly from
// src/utils/projectStatusConfig.js for new code.
export {
  PROJECT_STATUS,
  PROJECT_STATUS_OPTIONS,
  normalizeProjectStatus,
  getStatusLabel,
  getStatusClass,
  PROJECT_STATUS_COLORS,
} from '../utils/projectStatusConfig';

export const PROJECT_INVOICE_STATUS = {
  NOT_STARTED:   'not-started',
  PARTIAL:       'partial',
  FULLY_CLAIMED: 'fully-claimed',
  OVERDUE:       'overdue',
};

// ── Quotation (legacy `quotations` collection — bid/proposal docs) ────────────
export const QUOTATION_STATUS = {
  DRAFT:     'draft',
  SENT:      'sent',
  WON:       'won',
  LOST:      'lost',
  CANCELLED: 'cancelled',
};

export const QUOTATION_STATUS_OPTIONS = [
  { value: QUOTATION_STATUS.DRAFT,     label: 'Draft' },
  { value: QUOTATION_STATUS.SENT,      label: 'Sent' },
  { value: QUOTATION_STATUS.WON,       label: 'Won' },
  { value: QUOTATION_STATUS.LOST,      label: 'Lost' },
  { value: QUOTATION_STATUS.CANCELLED, label: 'Cancelled' },
];

// ── Sales Invoice Quotation (salesInvoiceQuotations — contract docs) ──────────
// NOTE: These values intentionally match what the existing UI expects.
export const SALES_QUOTATION_TYPE = {
  MAIN_CONTRACT: 'Sales - Main Contract',
  VO:            'Sales - VO',
  SUB_CON:       'Sub-Con Payments',
};

// ── Invoice ──────────────────────────────────────────────────────────────────
export const INVOICE_STATUS = {
  DRAFT:   'draft',
  SENT:    'sent',
  PARTIAL: 'partial',
  PAID:    'paid',
  OVERDUE: 'overdue',
};

// ── Sales Adjustments (salesAdjustments) ─────────────────────────────────────
// Money that falls outside normal scope-of-work billing — rounding waivers,
// goodwill, discounts, write-offs, back charges. Recorded as a separate ledger
// so contract amounts stay a faithful record of what was agreed, while balances
// and Claim Progress can still reach a true 100%.
//
// Scope determines which parent ids are set on the record:
//   quotation → quotationId + invoiceId + projectId
//   invoice   → invoiceId + projectId          (quotationId null)
//   project   → projectId                      (both others null)
// Every record has exactly one scope, so the set partitions cleanly and a
// project-level roll-up can absorb all three without double-counting.
export const ADJUSTMENT_SCOPE = {
  QUOTATION: 'quotation',
  INVOICE:   'invoice',
  PROJECT:   'project',
};

// Amounts are always stored POSITIVE; direction carries the sign. Staff never
// type a minus sign — the entry form uses a labelled toggle.
export const ADJUSTMENT_DIRECTION = {
  INCREASE: 'increase',  // we receive more
  DECREASE: 'decrease',  // we receive less
};

// Structured enum, never free text — Project Costing will aggregate on this
// later. Adding a value is safe; renaming one orphans existing records.
export const ADJUSTMENT_CATEGORY = {
  DISCOUNT:            'discount',
  GOODWILL:            'goodwill',
  ROUNDING_WAIVER:     'rounding_waiver',
  CREDIT_NOTE:         'credit_note',
  CONTRA:              'contra',
  OMIT_ITEMS:          'omit_items',
  OTHER:               'other',
};

// ── Expense Claims (staff reimbursements) ─────────────────────────────────────
export const CLAIM_STATUS = {
  PENDING:     'pending',
  APPROVED:    'approved',
  REJECTED:    'rejected',
  RESUBMITTED: 'resubmitted',
};

// ── Leave Records (Team Management) ────────────────────────────────────────────
export const LEAVE_TYPE = {
  SICK:             'sick',
  HOME:             'home',
  ANNUAL:           'annual',
  HALF_DAY:         'half_day',
  URGENT:           'urgent',
  NO_PAY:           'no_pay',
  HOSPITALIZATION:  'hospitalization',
};

// Deliberately ordered (not alphabetical) — render dropdowns with sort={false}
// to preserve this order.
export const LEAVE_TYPE_OPTIONS = [
  { value: LEAVE_TYPE.SICK,            label: 'Sick Leave / MC',       variant: 'red'    },
  { value: LEAVE_TYPE.HOME,            label: 'Home Leave',            variant: 'blue'   },
  { value: LEAVE_TYPE.ANNUAL,          label: 'Annual Leave',          variant: 'green'  },
  { value: LEAVE_TYPE.HALF_DAY,        label: 'Half Day Leave',        variant: 'teal'   },
  { value: LEAVE_TYPE.URGENT,          label: 'Urgent Leave',          variant: 'orange' },
  { value: LEAVE_TYPE.NO_PAY,          label: 'No Pay Leave',          variant: 'gray'   },
  { value: LEAVE_TYPE.HOSPITALIZATION, label: 'Hospitalization Leave', variant: 'purple' },
];

// ── Defects ───────────────────────────────────────────────────────────────────
export const DEFECT_STATUS = {
  OPEN:        'open',
  IN_PROGRESS: 'in-progress',
  DONE:        'done',
};

export const DEFECT_LIST_STATUS = {
  // New lists are created with ONGOING.
  // Legacy Firestore data may contain 'active' — listIsOngoing() handles both.
  ONGOING:   'ongoing',
  COMPLETED: 'completed',
  ARCHIVED:  'archived',
};

// ── Follow-Ups ────────────────────────────────────────────────────────────────
export const FOLLOW_UP_STATUS = {
  PENDING:     'pending',
  IN_PROGRESS: 'in-progress',
  COMPLETED:   'completed',
  CANCELLED:   'cancelled',
};

export const FOLLOW_UP_PRIORITY = {
  LOW:    'low',
  MEDIUM: 'medium',
  HIGH:   'high',
  URGENT: 'urgent',
};

// ── Project Follow-Ups (physical item lifecycle tracking) ────────────────────
// One record per physical item moving through: To Order → Ordered →
// To Be Delivered / Self-Collect → Delivered → Inventory.
// Lives in `projectFollowUps`, joined by projectId.
export const PROJECT_FOLLOW_UP_STATUS = {
  TO_ORDER:         'TO_ORDER',
  ORDERED:          'ORDERED',          // purchase order placed
  TO_BE_DELIVERED:  'TO_BE_DELIVERED',
  SELF_COLLECT:     'SELF_COLLECT',
  DELIVERED:        'DELIVERED',        // item received on site
  INVENTORY:        'INVENTORY',        // stored in inventory
  CANCELLED:        'CANCELLED',        // item no longer needed
};

export const PROJECT_FOLLOW_UP_STATUS_OPTIONS = [
  { value: PROJECT_FOLLOW_UP_STATUS.TO_ORDER,        label: 'To Order' },
  { value: PROJECT_FOLLOW_UP_STATUS.ORDERED,         label: 'Ordered' },
  { value: PROJECT_FOLLOW_UP_STATUS.TO_BE_DELIVERED, label: 'To Be Delivered' },
  { value: PROJECT_FOLLOW_UP_STATUS.SELF_COLLECT,    label: 'Self-Collect' },
  { value: PROJECT_FOLLOW_UP_STATUS.DELIVERED,       label: 'Delivered' },
  { value: PROJECT_FOLLOW_UP_STATUS.INVENTORY,       label: 'Inventory' },
  { value: PROJECT_FOLLOW_UP_STATUS.CANCELLED,       label: 'Cancelled' },
];

/** Unit of measurement options for follow-up items. */
export const PROJECT_FOLLOW_UP_UNIT = {
  NOS:   'nos',
  PCS:   'pcs',
  SET:   'set',
  ROLL:  'roll',
  YARD:  'yard',
  METRE: 'metre',
};

export const PROJECT_FOLLOW_UP_UNIT_OPTIONS = [
  { value: 'nos',   label: 'nos' },
  { value: 'pcs',   label: 'pcs' },
  { value: 'set',   label: 'set' },
  { value: 'roll',  label: 'roll' },
  { value: 'yard',  label: 'yard' },
  { value: 'metre', label: 'metre' },
];

export const PROJECT_FOLLOW_UP_DEFAULT_REMINDER_DAYS = 7;

// ── Follow Ups — Supplier Delivery ───────────────────────────────────────────
// deliveryType replaces the old `status` field for the new Supplier Delivery
// tab. Auto-status (TBC / Later / Arriving Soon / Arriving Today / Overdue) is
// computed client-side from deliveryDate + alertWindowDays — never stored.
export const DELIVERY_TYPE = {
  TO_BE_DELIVERED: 'TO_BE_DELIVERED',
  SELF_COLLECT:    'SELF_COLLECT',
  INVENTORY:       'INVENTORY',
};

export const DELIVERY_TYPE_OPTIONS = [
  { value: DELIVERY_TYPE.TO_BE_DELIVERED, label: 'To Be Delivered' },
  { value: DELIVERY_TYPE.SELF_COLLECT,    label: 'Self-Collect' },
  { value: DELIVERY_TYPE.INVENTORY,       label: 'Inventory' },
];

export const STORAGE_LOCATION_OPTIONS = [
  'Office',
  'Store Room',
  'Site',
  'With Me',
  'With Main Con',
  'With Architect',
  'With Supplier',
  'With Team Leader',
];

export const FOLLOW_UPS_AUTO_STATUS = {
  TBC:           'TBC',
  LATER:         'Later',
  ARRIVING_SOON: 'Arriving Soon',
  ARRIVING_TODAY:'Arriving Today',
  OVERDUE:       'Overdue',
};

// Default alert window (days). Configurable per-project in settings.
export const FOLLOW_UPS_DEFAULT_ALERT_WINDOW = 7;

// Alert window preset options shown in the gear modal.
export const FOLLOW_UPS_ALERT_WINDOW_OPTIONS = [5, 7, 10, 14, 30];

// ── Follow Ups — Driver Jobs ──────────────────────────────────────────────────
export const DRIVER_JOB_STATUS = {
  PENDING:      'pending',
  IN_PROGRESS:  'in_progress',
  ALL_COMPLETE: 'all_complete',
};

// ── Follow Ups — Sprint System ────────────────────────────────────────────────
export const SPRINT_BLOCK_TYPE = {
  TASK:    'task',
  H1:      'h1',
  H2:      'h2',
  H3:      'h3',
  TEXT:    'text',
  DIVIDER: 'divider',
  DATE:    'date',
  BOLD:    'bold',
  // ── Extended block types (Notes + SprintSystem) ──────────────────────────
  TOGGLE:   'toggle',   // collapsible toggle block
  TABLE:    'table',    // inline 2-col editable table
  BULLET:   'bullet',   // bulleted list item
  NUMBERED: 'numbered', // numbered list item
  CODE:     'code',     // inline code block
};

// ── Users ────────────────────────────────────────────────────────────────────
export const USER_ROLE = {
  WORKER:          'worker',
  TEAM_LEADER:     'team_leader',
  PROJECT_MANAGER: 'project_manager',
  ACCOUNTANT:      'accountant',
  ADMIN:           'admin',
  BOSSCONTROL:     'bosscontrol',
  BOSS:            'boss',
};

// REMOVED 2026-08 — PURCHASE_PM_PERSON_ROLES / PURCHASE_CARPENTER_PERSON_ROLES.
// They scoped the "Held By" person dropdown that RecordDeliveryModal showed for
// the 'with PM' / 'with Carpenter' locations. Both locations are retired (see
// PURCHASE_LOCATION_RETIRED above) and the person is now captured once on the
// PO line instead, so the picker and its role sets have no caller.

/**
 * Account lifecycle, stored as `status` on the user doc.
 *
 * A doc with NO status field is treated as ACTIVE — see isActiveUser() in
 * src/utils/userHelpers.js — for accounts created before the field existed.
 *
 * PENDING → ACTIVE (adminApproveUser) or REJECTED (adminRejectUser).
 * ACTIVE  → RETIRED (adminRetireUser) and back again (adminRestoreUser).
 *
 * RETIRED is the "they left, keep their history" state: the person disappears
 * from every picker (everything builds its list through isActiveUser) and can
 * no longer sign in, but their user doc survives so Purchasing, Attendance,
 * Staff Claim, Project Costing and payroll still resolve their name on old
 * records. Deleting them instead would orphan all of it. The `retiredAt`,
 * `retiredBy`, `retiredByName`, `retiredReason` and `retiredNote` fields
 * accompany it; Team Management's Past Staff tab reads them.
 */
export const USER_STATUS = {
  ACTIVE:   'active',
  PENDING:  'pending',
  REJECTED: 'rejected',
  RETIRED:  'retired',
};

/**
 * Why someone left. Structured rather than free text so the Past Staff list
 * stays filterable and consistent; the optional `retiredNote` carries the
 * detail. Adding a value is safe; renaming one orphans existing records.
 */
export const RETIREMENT_REASON = {
  RETIRED:        'retired',
  RESIGNED:       'resigned',
  CONTRACT_ENDED: 'contract_ended',
  TERMINATED:     'terminated',
};

export const RETIREMENT_REASON_OPTIONS = [
  { value: RETIREMENT_REASON.RETIRED,        label: 'Retired',        variant: 'blue'   },
  { value: RETIREMENT_REASON.RESIGNED,       label: 'Resigned / Quit', variant: 'orange' },
  { value: RETIREMENT_REASON.CONTRACT_ENDED, label: 'Contract Ended', variant: 'gray'   },
  { value: RETIREMENT_REASON.TERMINATED,     label: 'Terminated',     variant: 'red'    },
];

/**
 * Companies a worker or office staff member can belong to, stored as `company`
 * on the user doc. Set from Team Management; read by Attendance (worker rates,
 * Monthly tab filter) — one field, two modules, so keep it here rather than in
 * either module's own constants file.
 *
 * Deliberately ordered — render dropdowns with sort={false} to preserve it.
 *
 * NB: 'Stemcrafter' here is a legal entity. The unrelated COST_TYPE.STEMCRAFTER
 * above (~line 296) is a project-costing row category — different concept,
 * different collection, same word. They are joined in exactly ONE place —
 * purchaseInvoiceService.upsertCostingRowForLine, which routes a PO line bought
 * for a Stemcrafter staff member into the Stemcrafter cost tab. Nowhere else.
 */
export const COMPANIES = Object.freeze(['Stema Furniture', 'L&N', 'S&J', 'Stemcrafter']);

/**
 * The Stemcrafter legal entity, named rather than spelled out at the one call
 * site that compares against it — see the NB above. Derived from COMPANIES so
 * a rename of the entity can never leave the comparison pointing at a string
 * that no user doc can hold.
 */
export const STEMCRAFTER_COMPANY = COMPANIES[3];

/** Roles whose holders can be assigned as Project Manager. */
export const PM_ELIGIBLE_ROLES = [
  USER_ROLE.PROJECT_MANAGER,
  USER_ROLE.BOSS,
  USER_ROLE.BOSSCONTROL,
  USER_ROLE.ADMIN,
];

// ── Works Catalogue ───────────────────────────────────────────────────────────
/**
 * What staff see this module called — sidebar, page title, breadcrumbs,
 * permissions, messages. Every visible mention reads it from here. The code's
 * own names (WorksCatalogue*, works_catalogue, /works-catalogue) predate the
 * rename and are deliberately left alone: changing a permission key or a URL
 * means migrating stored grants and breaking saved links, for no visible gain.
 */
export const WORK_CATALOGUE_NAME = 'Work Catalogue';

// WORK_FAMILY, CATALOGUE_FILE_KIND, CATALOGUE_LENS and CATALOGUE_SORT were
// removed with the items/details catalogue (2026-10). Legacy catalogueItems
// docs still carry workFamily ('renovation'|'furniture') — data only.

// ── Query limits ──────────────────────────────────────────────────────────────
// Safety caps — prevents unbounded reads while analytics summary docs are not yet live.
// Increase if you legitimately have more documents in a collection.
export const QUERY_LIMITS = {
  PROJECTS:         200,
  QUOTATIONS:       500,
  CLAIMS:           100,
  DEFECTS:          500,
  USERS:            200,
  DEFAULT:          200,
  INVOICE_PAYMENTS: 2000,
  PURCHASING:       1000,
  INVENTORY_ITEMS:  1000,
  INVENTORY_MOVES:  2000,   // LEGACY — kept for migration reads
  // Every inventory surface loads the WHOLE ledger and filters/aggregates it
  // client-side (Inventory, Factory Stock Costing, per-project detail, Movement
  // History) — there is no server-side paging or ordering on inventoryLedger, by
  // design: see subscribeInventoryLedger and the no-composite-index notes in
  // inventoryService.js.
  //
  // That makes this cap a correctness boundary, not just a performance one: at
  // the cap the ledger is TRUNCATED, and stock-costing and shrinkage figures
  // silently understate rather than visibly fail. Raised from 4000 for headroom
  // as issue/withdrawal volume grows. MovementHistory.jsx surfaces a banner when
  // a load actually reaches this number, so the truncation can never be silent —
  // if that banner ever appears, this is no longer the right architecture and
  // the ledger needs server-side paging (which in turn needs the composite
  // indexes that decision has so far avoided).
  INVENTORY_LEDGER: 20000,
  PURCHASE_ORDERS:  1000,
  DELIVERY_ORDERS:  2000,
  PURCHASE_INVOICES:2000,
  // Catalogue home loads every photo once to count and pick a cover per
  // project. Past ~5k photos, denormalise count + cover onto the project.
  CATALOGUE_PHOTOS:  10000,
};

// ── Timestamp helpers ─────────────────────────────────────────────────────────
/**
 * Use nowTs() for createdAt / updatedAt in every Firestore write.
 * Returns Firebase serverTimestamp() sentinel.
 */
export const nowTs = () => serverTimestamp();

/**
 * Optimistic placeholder for React state after a write.
 * Use this in setX(prev => ...) calls — NEVER pass to Firestore.
 */
export const nowDate = () => new Date();
